import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { PostsGroupMembersEmployeeIdsDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import type { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useQuery } from 'modules/core/hooks/useQuery'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import useGroupMemberships from 'modules/dashboard/hooks/useGroupMemberships'
import accessGroupMemberships from 'modules/dashboard/store/collections/AccessGroupMemberships'
import groups from 'modules/dashboard/store/collections/communities/Groups'
import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'
import type Membership from 'modules/teams/store/models/Membership'

export type Action = 'add' | 'remove'
const ACTIONS: Record<Action, string> = {
  add: 'add_participants',
  remove: 'remove_participants',
} as const

interface Props {
  onClose: () => void
  groupId: number
  action: Action
}

function ManageMembersGroup({ groupId, action, onClose }: Props) {
  const [running, setRunning] = React.useState(false)
  const [members, setMembers] = React.useState<EmployeeForSelectFragmentNoAccess[]>([])

  const group = groups.find({
    id: groupId,
  })

  const accessGroup = group?.accessGroup
  const { loading: loadingGroupMemberships } = useGroupMemberships(groupId)

  const currentMemberAccessIds = React.useMemo(() => {
    if (!accessGroup) return []

    const accessIds = accessGroupMemberships
      .toArray()
      .filter((membership) => membership.attributes.access_group_id === accessGroup.id)
      .map((membership) => membership.attributes.access_id)
      .filter((id): id is number => id !== undefined)

    return accessIds
  }, [accessGroup, loadingGroupMemberships])

  const { data, loading: loadingEmployees } = useQuery(PostsGroupMembersEmployeeIdsDocument, {
    variables: {
      accessIds: currentMemberAccessIds,
    },
    skip: currentMemberAccessIds.length === 0 || loadingGroupMemberships,
  })

  const currentMemberEmployeeIds =
    data?.employees?.employeeNames?.map((employee) => employee.id) || []

  const submit = async (closePortal: ClosePortalFn) => {
    if (!accessGroup) return

    setRunning(true)

    const { success, value: memberships } = await asyncAction(() =>
      group.rpc(ACTIONS[action], {
        employee_ids: members.map((member) => member.id),
      })
    )
    setRunning(false)

    if (success) {
      if (action === 'remove') {
        accessGroupMemberships.remove(memberships.map((membership: Membership) => membership.id))
      } else {
        accessGroupMemberships.add(memberships)
      }
      closePortal()
    }
  }

  const removeModalInfo = {
    title: i18n.t('dashboard.groups.remove_members.title'),
    description: i18n.t('dashboard.groups.remove_members.description'),
    submit: i18n.t('dashboard.groups.remove_members.submit'),
  }

  const addModalInfo = {
    title: i18n.t('dashboard.groups.add_members.title'),
    description: i18n.t('dashboard.groups.add_members.description'),
    submit: i18n.t('dashboard.groups.add_members.submit'),
  }

  const modalInfo = action === 'remove' ? removeModalInfo : addModalInfo

  return (
    <Modal
      disableFocusTrap
      description={modalInfo.description}
      illustration="add-user"
      onClose={onClose}
      onSubmit={submit}
      title={modalInfo.title}
      footer={
        <Button disabled={!members.length || running} submit>
          {modalInfo.submit}
        </Button>
      }
    >
      {loadingGroupMemberships || loadingEmployees ? (
        <Loading />
      ) : (
        <Box maxWidth="s360">
          <EmployeeSelector
            multiple
            value={members}
            withAvatar
            excludeCurrentEmployee
            employeeIds={action === 'remove' ? currentMemberEmployeeIds : undefined}
            excludeIds={action === 'add' ? currentMemberEmployeeIds : []}
            onSelect={setMembers}
            allowedGroups={['all', 'teams', 'managers', 'workplaces']}
          />
        </Box>
      )}
    </Modal>
  )
}

export default observer(ManageMembersGroup)
